import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, BellRing, Clock, Fingerprint, Loader2, RefreshCw, Search, Settings, Smartphone, Trash2, Users, Zap } from 'lucide-react';
import { EmptyState, StatCard, StatsGrid } from '../../components/common';
import { Modal } from '../../components/common/modals';
import { REFRESH_INTERVALS, formatInterval, useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useTriggerFullReload } from '../../hooks/useConfigurationVersion';
import { useRegisteredDeliverySettings, useUpdateRegisteredDeliveryDeploymentId } from '../../hooks/useConfigurationApi';
import { normalizePushGatewayDeploymentId } from '../../services/configurationApi';
import {
  useCleanupExpiredSipRegistrations,
  useRevokePushBindings,
  usePushGatewayCapabilities,
  usePushGatewayDeliveries,
  usePushGatewayRegistrations,
  usePushGatewayStatus,
  useSendTestPush,
  type PushGatewayDeliveryStatus,
  type PushGatewayRegistration,
  type PushGatewayTestPushResult,
} from '../../hooks/usePushGateway';

const pageSize = 50;

function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return '-';
  const parsed = Date.parse(timestamp);
  if (!Number.isFinite(parsed)) return timestamp;
  return new Date(parsed).toLocaleString();
}

function activeBadgeClass(isActive: boolean): string {
  return isActive ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800';
}

function deliveryStatusBadgeClass(status: PushGatewayDeliveryStatus): string {
  switch (status) {
    case 'accepted':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    case 'no_registered_device':
      return 'bg-amber-100 text-amber-800';
  }
}

function parseDeliveryStatusFilter(value: string): PushGatewayDeliveryStatus | '' {
  switch (value) {
    case 'accepted':
    case 'rejected':
    case 'no_registered_device':
      return value;
    default:
      return '';
  }
}

function RegisteredDeliverySettingsEditor() {
  const [deploymentId, setDeploymentId] = useState('');
  const [savedDeploymentId, setSavedDeploymentId] = useState('');
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const registeredDeliverySettingsQuery = useRegisteredDeliverySettings();
  const updateDeploymentId = useUpdateRegisteredDeliveryDeploymentId();

  useEffect(() => {
    if (!settingsLoaded && registeredDeliverySettingsQuery.data !== undefined) {
      const loadedDeploymentId = registeredDeliverySettingsQuery.data?.pushGatewayDeploymentId ?? '';
      setDeploymentId(loadedDeploymentId);
      setSavedDeploymentId(loadedDeploymentId);
      setSettingsLoaded(true);
    }
  }, [registeredDeliverySettingsQuery.data, settingsLoaded]);

  const handleSave = () => {
    const value = normalizePushGatewayDeploymentId(deploymentId);
    if (value === null) return;
    updateDeploymentId.mutate(value, {
      onSuccess: (settings) => {
        const savedValue = settings.pushGatewayDeploymentId ?? value;
        setDeploymentId(savedValue);
        setSavedDeploymentId(savedValue);
        toast.success('Gateway deployment ID saved');
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to save gateway deployment ID');
      },
    });
  };

  const normalizedDeploymentId = normalizePushGatewayDeploymentId(deploymentId);
  const deploymentIdValidationMessage = deploymentId.trim().length > 1024
    ? 'Deployment ID must be 1024 characters or fewer.'
    : undefined;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm" aria-labelledby="gateway-deployment-settings-title">
      <div className="max-w-2xl">
        <h2 id="gateway-deployment-settings-title" className="text-lg font-semibold text-gray-900">Gateway deployment settings</h2>
        <p className="mt-1 text-sm text-gray-600">Set the public deployment ID used by the push gateway for this deployment.</p>
        <div className="mt-4">
          {registeredDeliverySettingsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-600"><Loader2 className="h-4 w-4 animate-spin text-blue-600" /> Loading deployment settings...</div>
          ) : registeredDeliverySettingsQuery.isError ? (
            <p className="text-sm text-red-700" role="alert">Unable to load gateway deployment settings. {registeredDeliverySettingsQuery.error instanceof Error ? registeredDeliverySettingsQuery.error.message : 'Please try again.'}</p>
          ) : (
            <form onSubmit={(event) => { event.preventDefault(); handleSave(); }} className="space-y-3">
              <label htmlFor="push-gateway-deployment-id" className="block text-sm font-medium text-gray-700">Gateway deployment ID</label>
              <input
                id="push-gateway-deployment-id"
                type="text"
                required
                maxLength={1024}
                value={deploymentId}
                onChange={(event) => setDeploymentId(event.target.value)}
                placeholder="Enter the deployment ID configured for the API key"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                aria-invalid={deploymentIdValidationMessage !== undefined}
                aria-describedby={deploymentIdValidationMessage === undefined ? 'push-gateway-deployment-id-help' : 'push-gateway-deployment-id-help push-gateway-deployment-id-error'}
              />
              <p id="push-gateway-deployment-id-help" className="text-xs text-gray-500">This must match the deployment ID allowed by the gateway API key. It is not the site label, token, or gateway secret.</p>
              {deploymentIdValidationMessage !== undefined && <p id="push-gateway-deployment-id-error" className="text-sm text-red-700" role="alert">{deploymentIdValidationMessage}</p>}
              <div className="flex items-center gap-3">
                <button type="submit" disabled={normalizedDeploymentId === null || normalizedDeploymentId === savedDeploymentId || updateDeploymentId.isPending} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                  {updateDeploymentId.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save deployment ID
                </button>
                {updateDeploymentId.isSuccess && <span className="text-sm text-green-700" role="status">Saved.</span>}
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export function PushGatewayPage() {
  const [activeOnly, setActiveOnly] = useState(false);
  const [domain, setDomain] = useState('');
  const [page, setPage] = useState(1);
  const [deliveryAor, setDeliveryAor] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState<PushGatewayDeliveryStatus | ''>('');
  const [deliveryCursor, setDeliveryCursor] = useState<string | undefined>(undefined);
  const [showSettings, setShowSettings] = useState(false);
  const [registrationToRevoke, setRegistrationToRevoke] = useState<PushGatewayRegistration | null>(null);
  const [registrationToTest, setRegistrationToTest] = useState<PushGatewayRegistration | null>(null);
  const [testPushResult, setTestPushResult] = useState<PushGatewayTestPushResult | null>(null);
  const [showReloadConfirm, setShowReloadConfirm] = useState(false);

  const autoRefresh = useAutoRefresh({
    key: 'push_gateway_autorefresh',
    defaultEnabled: true,
    defaultInterval: 5000,
  });

  const filters = useMemo(() => ({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    activeOnly,
    ...(domain.trim() ? { domain: domain.trim() } : {}),
  }), [activeOnly, domain, page]);

  const deliveryFilters = useMemo(() => ({
    limit: pageSize,
    ...(deliveryAor.trim() ? { aor: deliveryAor.trim() } : {}),
    ...(deliveryStatus ? { status: deliveryStatus } : {}),
    ...(deliveryCursor === undefined ? {} : { cursor: deliveryCursor }),
  }), [deliveryAor, deliveryCursor, deliveryStatus]);

  const refetchInterval = autoRefresh.enabled ? autoRefresh.interval : false;
  const capabilitiesQuery = usePushGatewayCapabilities();
  const pushGatewayConfigured = capabilitiesQuery.data?.data.configured === true;
  const statusQuery = usePushGatewayStatus(refetchInterval, pushGatewayConfigured);
  const registrationsQuery = usePushGatewayRegistrations(filters, refetchInterval, pushGatewayConfigured);
  const deliveriesQuery = usePushGatewayDeliveries(deliveryFilters, refetchInterval, pushGatewayConfigured);
  const revokePushBindings = useRevokePushBindings();
  const sendTestPush = useSendTestPush();
  const cleanupExpired = useCleanupExpiredSipRegistrations();
  const triggerFullReload = useTriggerFullReload();

  const status = statusQuery.data?.data;
  const registrations = registrationsQuery.data?.data ?? [];
  const deliveryResult = deliveriesQuery.data?.data;
  const deliveries = deliveryResult?.deliveries ?? [];
  const pagination = registrationsQuery.data?.pagination;
  const totalPages = pagination?.totalPages ?? 0;
  const providerBreakdown = status?.byProvider.map((item) => `${item.provider}: ${item.count}`).join(', ') || 'No providers yet';

  const handleActiveOnlyChange = (value: boolean) => {
    setActiveOnly(value);
    setPage(1);
  };

  const handleDomainChange = (value: string) => {
    setDomain(value);
    setPage(1);
  };

  const refreshAll = () => {
    statusQuery.refetch();
    registrationsQuery.refetch();
    deliveriesQuery.refetch();
  };

  const handleDeliveryAorChange = (value: string) => {
    setDeliveryAor(value);
    setDeliveryCursor(undefined);
  };

  const handleDeliveryStatusChange = (value: string) => {
    setDeliveryStatus(parseDeliveryStatusFilter(value));
    setDeliveryCursor(undefined);
  };

  const confirmRevokePushBindings = () => {
    if (!registrationToRevoke) return;
    revokePushBindings.mutate(registrationToRevoke.aor, {
      onSuccess: (response) => {
        toast.success(`Revoked PB bindings for ${response.data.aor} (${response.data.revokedCount} row${response.data.revokedCount === 1 ? '' : 's'})`);
        setRegistrationToRevoke(null);
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to revoke PB bindings');
      },
    });
  };

  const confirmSendTestPush = () => {
    if (!registrationToTest) return;
    sendTestPush.mutate(registrationToTest.aor, {
      onSuccess: (response) => {
        const acceptedCount = response.data.deliveries.filter((delivery) => delivery.accepted).length;
        const rejectedCount = response.data.deliveries.length - acceptedCount;
        setTestPushResult(response.data);
        toast.success(`Sent test call push for ${response.data.aor}: ${acceptedCount} accepted, ${rejectedCount} rejected`);
        setRegistrationToTest(null);
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to send test push');
      },
    });
  };

  const handleCleanupExpired = () => {
    if (!window.confirm('Cleanup expired SIP registrations? This removes expired registration rows and may reduce expired wakeable push bindings.')) return;
    cleanupExpired.mutate(undefined, {
      onSuccess: (response) => {
        const count = response.data.deletedCount;
        toast.success(`Cleaned up ${count} expired registration${count === 1 ? '' : 's'}`);
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to cleanup expired registrations');
      },
    });
  };

  const handleFullReload = () => {
    triggerFullReload.mutate('Manual full reload from Push Gateway page', {
      onSuccess: () => {
        toast.success('Full configuration reload triggered — engine restarting');
        setShowReloadConfirm(false);
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to trigger full reload');
      },
    });
  };

  if (!pushGatewayConfigured) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Push Gateway</h1>
          <p className="mt-1 max-w-3xl text-gray-600">
            Monitor RFC 8599 push bindings stored in SIP registrations.
          </p>
        </div>
        <RegisteredDeliverySettingsEditor />
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
          <EmptyState icon={AlertTriangle} title="Push gateway is not configured in this deployment" description="Configure registered-delivery settings and the push webhook secret reference to enable this page." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Push Gateway</h1>
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
              {status?.total ?? 0} Bindings
            </span>
            {autoRefresh.enabled && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                Auto-refresh {formatInterval(autoRefresh.interval)}
              </span>
            )}
          </div>
          <p className="max-w-3xl text-gray-600">
            Monitor RFC 8599 push bindings stored in SIP registrations. APNs credentials and push gateway settings live in registered-delivery settings and are not shown here.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={refreshAll} disabled={statusQuery.isFetching || registrationsQuery.isFetching || deliveriesQuery.isFetching} className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${statusQuery.isFetching || registrationsQuery.isFetching || deliveriesQuery.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" onClick={handleCleanupExpired} disabled={cleanupExpired.isPending} className="flex items-center gap-2 rounded-lg border border-amber-300 px-3 py-2 text-amber-800 transition-colors hover:bg-amber-50 disabled:opacity-50">
            <Trash2 className="h-4 w-4" />
            Cleanup expired registrations
          </button>
          <button type="button" onClick={() => setShowReloadConfirm(true)} disabled={triggerFullReload.isPending} className="flex items-center gap-2 rounded-lg border border-blue-300 px-3 py-2 text-blue-800 transition-colors hover:bg-blue-50 disabled:opacity-50">
            <Zap className="h-4 w-4" />
            Full config reload
          </button>

          <div className="relative">
            <button type="button" onClick={() => setShowSettings((value) => !value)} className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Settings className="h-4 w-4" />
              Settings
            </button>
            {showSettings && (
              <div className="absolute right-0 z-10 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Auto-refresh</h3>
                <label className="mb-3 flex items-center justify-between text-sm text-gray-700">
                  Enabled
                  <input type="checkbox" checked={autoRefresh.enabled} onChange={(event) => autoRefresh.setEnabled(event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                </label>
                <select value={autoRefresh.interval} onChange={(event) => autoRefresh.setInterval(Number(event.target.value))} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500">
                  {REFRESH_INTERVALS.map((interval) => <option key={interval.value} value={interval.value}>{interval.label}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      <StatsGrid columns={5}>
        <StatCard label="Total push bindings" value={status?.total ?? 0} icon={BellRing} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
        <StatCard label="Active" value={status?.active ?? 0} icon={Smartphone} iconBgColor="bg-green-100" iconColor="text-green-600" valueColor="text-green-700" />
        <StatCard label="Expired wakeable" value={status?.expiredWakeable ?? 0} icon={Clock} iconBgColor="bg-amber-100" iconColor="text-amber-600" valueColor="text-amber-700" />
        <StatCard label="Distinct AORs" value={status?.distinctAors ?? 0} icon={Users} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
        <StatCard label="Providers" value={status?.byProvider.length ?? 0} icon={Fingerprint} iconBgColor="bg-slate-100" iconColor="text-slate-600" subtitle={providerBreakdown} />
      </StatsGrid>

      <RegisteredDeliverySettingsEditor />

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input type="text" value={domain} onChange={(event) => handleDomainChange(event.target.value)} placeholder="Filter by domain" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700">
            <input type="checkbox" checked={activeOnly} onChange={(event) => handleActiveOnlyChange(event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            Active only
          </label>
          {(domain || activeOnly) && (
            <button type="button" onClick={() => { setDomain(''); setActiveOnly(false); setPage(1); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {registrationsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
        ) : registrations.length === 0 ? (
          <EmptyState icon={BellRing} title="No push-capable registrations" description="RFC 8599 push bindings will appear here when SIP registrations include push parameters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">AOR</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User / Domain</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">PB provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">PB topic</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">PB token fingerprint</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status / Expiry</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {registrations.map((registration) => (
                  <tr key={registration.id} className="hover:bg-gray-50">
                    <td className="max-w-xs px-6 py-4"><div className="break-all font-mono text-sm text-gray-900">{registration.aor}</div></td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div>{registration.username ?? '-'}</div>
                      <div className="text-xs text-gray-500">{registration.domain ?? '-'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{registration.provider}</td>
                    <td className="max-w-xs px-6 py-4"><div className="break-all text-sm text-gray-700">{registration['pn-param'] ?? '-'}</div></td>
                    <td className="px-6 py-4 font-mono text-sm text-gray-700">{registration.tokenFingerprint ?? '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${activeBadgeClass(registration.isActive)}`}>{registration.isActive ? 'Active' : 'Expired'}</span>
                      <div className="mt-1 text-xs text-gray-500">{formatTimestamp(registration.expiresAt)}</div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setRegistrationToTest(registration)} disabled={sendTestPush.isPending} className="inline-flex items-center gap-1 rounded-md border border-blue-300 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50">
                          <BellRing className="h-3.5 w-3.5" /> Send test push
                        </button>
                        <button type="button" onClick={() => setRegistrationToRevoke(registration)} disabled={revokePushBindings.isPending} className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                          <Trash2 className="h-3.5 w-3.5" /> Revoke PB bindings
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {testPushResult && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-blue-900">Latest test call push result</h3>
              <p className="mt-1 break-all text-sm text-blue-800">AOR: {testPushResult.aor} · Call-ID: {testPushResult.callId}</p>
            </div>
            <button type="button" onClick={() => setTestPushResult(null)} className="text-sm font-medium text-blue-800 hover:text-blue-950">Dismiss</button>
          </div>
          <div className="space-y-2">
            {testPushResult.deliveries.length === 0 ? (
              <p className="text-sm text-blue-800">Gateway returned no delivery rows.</p>
            ) : testPushResult.deliveries.map((delivery) => (
              <div key={delivery.deliveryId} className="rounded-md bg-white p-3 text-sm shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${delivery.accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {delivery.accepted ? 'Accepted' : 'Rejected'}
                  </span>
                  <span className="font-mono text-gray-800">{delivery.status}</span>
                  <span className="font-mono text-xs text-gray-500">{delivery.deliveryId}</span>
                </div>
                {delivery.reason && <p className="mt-2 text-gray-700">Reason: {delivery.reason}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Recent push deliveries</h2>
              <p className="mt-1 text-sm text-gray-600">Hosted gateway delivery attempts for this deployment.</p>
            </div>
            <button type="button" onClick={() => deliveriesQuery.refetch()} disabled={deliveriesQuery.isFetching} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${deliveriesQuery.isFetching ? 'animate-spin' : ''}`} />
              Refresh deliveries
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input type="text" value={deliveryAor} onChange={(event) => handleDeliveryAorChange(event.target.value)} placeholder="Filter deliveries by AOR" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:ring-blue-500" />
            </div>
            <select value={deliveryStatus} onChange={(event) => handleDeliveryStatusChange(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500">
              <option value="">All statuses</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="no_registered_device">No registered device</option>
            </select>
            {(deliveryAor || deliveryStatus || deliveryCursor) && (
              <button type="button" onClick={() => { setDeliveryAor(''); setDeliveryStatus(''); setDeliveryCursor(undefined); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Clear delivery filters
              </button>
            )}
          </div>
        </div>
        {deliveriesQuery.isLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
        ) : deliveries.length === 0 ? (
          <EmptyState icon={BellRing} title="No recent push deliveries" description="Delivery attempts will appear here after the hosted gateway processes pushes." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">AOR</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Caller → Callee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">APNs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Token</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {deliveries.map((delivery) => (
                  <tr key={delivery.deliveryId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">{formatTimestamp(delivery.createdAt)}</td>
                    <td className="max-w-xs px-6 py-4"><div className="break-all font-mono text-sm text-gray-900">{delivery.aor ?? '-'}</div></td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div>{delivery.caller}</div>
                      <div className="text-xs text-gray-500">→ {delivery.callee}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${deliveryStatusBadgeClass(delivery.status)}`}>{delivery.status}</span>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm text-gray-700">{delivery.apnsStatus ?? '-'}</td>
                    <td className="max-w-xs px-6 py-4"><div className="break-words text-sm text-gray-700">{delivery.reason ?? '-'}</div></td>
                    <td className="px-6 py-4 font-mono text-sm text-gray-700">{delivery.tokenFingerprint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(deliveryCursor || deliveryResult?.cursor) && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <button type="button" onClick={() => setDeliveryCursor(undefined)} disabled={deliveryCursor === undefined} className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50">First page</button>
            <button type="button" onClick={() => setDeliveryCursor(deliveryResult?.cursor)} disabled={deliveryResult?.cursor === undefined} className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50">Next page</button>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">Page {page} of {totalPages} · {pagination?.total ?? 0} total</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1} className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50">Previous</button>
            <button type="button" onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={page >= totalPages} className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      <Modal isOpen={registrationToRevoke !== null} onClose={() => { if (!revokePushBindings.isPending) setRegistrationToRevoke(null); }} title="Revoke PB bindings" subtitle="Revoke persisted PB push bindings for this AOR" size="sm" closeOnBackdrop={!revokePushBindings.isPending} closeOnEscape={!revokePushBindings.isPending} footer={(
        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => setRegistrationToRevoke(null)} disabled={revokePushBindings.isPending} className="rounded-lg px-4 py-2 text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={confirmRevokePushBindings} disabled={revokePushBindings.isPending} className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
            {revokePushBindings.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Revoke PB bindings
          </button>
        </div>
      )}>
        <div className="space-y-4 text-sm text-gray-700">
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <p>This destructive action revokes the persisted PB push bindings for all registration rows matching the AOR. It does not edit gateway credentials or unrelated registration data.</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="mb-1 text-xs font-medium uppercase text-gray-500">AOR</p>
            <p className="break-all font-mono text-gray-900">{registrationToRevoke?.aor}</p>
          </div>
        </div>
      </Modal>

      <Modal isOpen={registrationToTest !== null} onClose={() => { if (!sendTestPush.isPending) setRegistrationToTest(null); }} title="Send test call push" subtitle="Send a real hosted-gateway VoIP push to this registration" size="sm" closeOnBackdrop={!sendTestPush.isPending} closeOnEscape={!sendTestPush.isPending} footer={(
        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => setRegistrationToTest(null)} disabled={sendTestPush.isPending} className="rounded-lg px-4 py-2 text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={confirmSendTestPush} disabled={sendTestPush.isPending} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
            {sendTestPush.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Send test push
          </button>
        </div>
      )}>
        <div className="space-y-4 text-sm text-gray-700">
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <p><strong>This sends a real VoIP push.</strong> The hosted gateway only sends VoIP pushes, and the iOS app always reports them as a CallKit incoming call, so the target device will ring with an incoming test call.</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="mb-1 text-xs font-medium uppercase text-gray-500">AOR</p>
            <p className="break-all font-mono text-gray-900">{registrationToTest?.aor}</p>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showReloadConfirm} onClose={() => { if (!triggerFullReload.isPending) setShowReloadConfirm(false); }} title="Full configuration reload" subtitle="Restart the SIP engine to reload configuration from the database" size="sm" closeOnBackdrop={!triggerFullReload.isPending} closeOnEscape={!triggerFullReload.isPending} footer={(
        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => setShowReloadConfirm(false)} disabled={triggerFullReload.isPending} className="rounded-lg px-4 py-2 text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={handleFullReload} disabled={triggerFullReload.isPending} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
            {triggerFullReload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Trigger reload
          </button>
        </div>
      )}>
        <p className="text-sm text-gray-700">A full reload restarts the SIP engine gracefully. Active calls may be briefly affected during the restart.</p>
      </Modal>
    </div>
  );
}
